<template>
  <layout-public>
    <page-meta />

    <page-hero
      :crumbs="[
        { label: t('Home'), href: route('index') },
        { label: t('Promotions'), href: route('promotions') },
        { label: t('RM 299 Landing Page Promo') }
      ]"
      :title="t('RM 299 Landing Page Promo')"
      :lede="t('One page that does exactly one job and nothing else: turning the visitors who land on it into genuine, qualified enquiries for your business.')"
    />

    <!-- The offer band and the comparison band are shared with the annual pack
         page, so the two offers cannot drift apart again. RM 299's window has
         closed, so the shared band renders nothing that ticks. -->
    <money-band
      :kicker="t('One page, one payment')"
      :title="t('One page should not lock you into a year')"
      :lede="t('Most web design quotes cover a multi-page website billed across the year. Need only one page? RM 299 covers it once, hosting and SSL included.')"
      :clock-label="t('Promotion expired')"
      poster="/images/promotions/rm299-landing-page.png"
      :poster-alt="t('RM 299 landing page offer')"
    />

    <!-- The comparison, split off from the offer band so the poster and the
         offer window sit with the heading and the figures get their own panel
         below it. -->
    <compare
      :kicker="t('RM 696 a year, or RM 299 once')"
      :title="t('How much you can save')"
      :lede="t('Both columns cover your first year. One is a yearly contract for a multi-page website; the other is a single one-off payment that never renews.')"
      :panels="panels"
      :stats="stats"
    />

    <section class="week" id="week">
      <div class="container">
        <section-title
          align="left"
          data-aos="fade-up"
          :kicker="t('The week it takes')"
          :title="t('From your materials to live in about a week')"
          :lede="t('Four straightforward steps, from the day you send your materials across to the day the page is finally live, and we keep you posted throughout.')"
        />

        <ol class="week-track">
          <li v-for="step in week" :key="step.when" class="week-step" data-aos="fade-up">
            <span class="week-node" aria-hidden="true"></span>
            <p class="week-when">{{ step.when }}</p>
            <h3>{{ step.title }}</h3>
            <p class="week-body">{{ step.body }}</p>
          </li>
        </ol>
      </div>
    </section>

    <manifest
      :kicker="t('What you get')"
      :title="t('Everything in the RM 299 package')"
      :lede="t('No small print, no asterisk, no renewal invoice afterwards. Here is precisely what you get once the page goes live and the work is finished.')"
      :items="included"
    />

    <brief
      :kicker="t('To start the build')"
      :title="t('What we need from you')"
      :lede="t('Send these straight over and we start immediately. The sooner they reach us, the sooner the page goes live and starts working properly for you.')"
      :items="checklist"
    />

    <faq-list
      :kicker="t('Before you decide')"
      :title="t('Frequently asked questions')"
      :lede="t('What the RM 299 package actually includes, what remains with you for life, and exactly what happens on the day the page eventually goes live.')"
      :items="faqs"
      accordion-id="rm299-faq"
    />
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* ---- The week: a dated rail rather than another card grid --------------- */
.week {
  background: $ground;
  position: relative;

  /* The blueprint ground the services section and the one-off band carry: two
     soft blue tints plus a hairline lattice, masked so the grid eases out
     before the section ends rather than ruling right to the edge. The section
     is white, so the lines stay near-black at 5% and read as paper, not ink. */
  &::before {
    background-image:
      radial-gradient(58rem 30rem at 12% -8%, $tint-primary-soft, transparent 68%),
      radial-gradient(48rem 26rem at 92% 104%, rgba($accent-blue, .08), transparent 68%),
      linear-gradient(rgba($text-heading, .05) 1px, transparent 1px),
      linear-gradient(90deg, rgba($text-heading, .05) 1px, transparent 1px);
    background-size: auto, auto, 4.5rem 4.5rem, 4.5rem 4.5rem;
    content: '';
    inset: 0;
    mask-image: linear-gradient(#000, rgb(0 0 0 / 0%) 88%);
    pointer-events: none;
    position: absolute;
    -webkit-mask-image: linear-gradient(#000, rgb(0 0 0 / 0%) 88%);
  }

  /* Lifted above the pattern. The step dots fill with #fff, so they need to
     sit over the grid rather than under it. */
  > .container {
    position: relative;
  }
}

.week-track {
  display: grid;
  gap: 2rem;
  grid-template-columns: minmax(0, 1fr);
  list-style: none;
  margin: 0;
  padding: 0;

  /* Phones get a rail down the left of the steps. */
  @media (max-width: 991.98px) {
    border-left: 1px solid $line-blue;
  }

  /* Desktop turns the same rail through ninety degrees and runs it across
     the top, so the four steps read left to right in the order they happen. */
  @media (min-width: 992px) {
    border-top: 1px solid $line-blue;
    gap: 2.5rem;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.week-step {
  padding-left: 1.5rem;
  position: relative;

  @media (min-width: 992px) {
    padding: 1.75rem 1.5rem 0 0;
  }
}

/* Sits astride the rail, so every step is marked on it. */
.week-node {
  background: $ground;
  border: 2px solid $primary;
  border-radius: 50%;
  height: 0.7rem;
  left: -0.35rem;
  position: absolute;
  top: 0.4rem;
  width: 0.7rem;

  @media (min-width: 992px) {
    left: 0;
    top: -0.36rem;
  }
}

/* The step label is the shared $label-* step, not a 0.72rem copy of it. It was
   the sixth place the same small uppercase label had been spelled out by hand,
   and it is a label above a title, so it takes the light ground's colour along
   with the type. */
.week-when {
  color: $label-on-light;
  font-family: $headings-font-family;
  font-size: $label-size;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  margin-bottom: 0.6rem;
  text-transform: uppercase;
}

.week-step h3 {
  color: $text-heading;
  font-size: 1.1rem;
  line-height: 1.3;
  margin-bottom: 0.4rem;
}

.week-body {
  color: $text-label;
  font-size: 0.92rem;
  line-height: 1.6;
  margin-bottom: 0;
}

</style>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PageMeta from '@/Components/PageMeta.vue';
import FaqList from '@/Components/FaqList.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import PageHero from '@/Components/PageHero.vue';
import Brief from '@/Pages/Promotions/Sections/Brief.vue';
import Compare from '@/Pages/Promotions/Sections/Compare.vue';
import Manifest from '@/Pages/Promotions/Sections/Manifest.vue';
import MoneyBand from '@/Pages/Promotions/Sections/MoneyBand.vue';
import SectionTitle from '@/Components/SectionTitle.vue';

export default {
  components: {
    Compare,
    FaqList,
    PageMeta,
    LayoutPublic,
    Manifest,
    MoneyBand,
    PageHero,
    Brief,
    SectionTitle
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'One hand-coded landing page for RM 299. Hosting, your own .vxms.dev address and SSL included, with nothing monthly. Live in about a week.': 'One hand-coded landing page for RM 299. Hosting, your own .vxms.dev address and SSL included, with nothing monthly. Live in about a week.',
          '1 week': '1 week',
          'Day 0': 'Day 0',
          'Day 1': 'Day 1',
          'Day 4': 'Day 4',
          'Day 5': 'Day 5',
          'Four straightforward steps, from the day you send your materials across to the day the page is finally live, and we keep you posted throughout.': 'Four straightforward steps, from the day you send your materials across to the day the page is finally live, and we keep you posted throughout.',
          'From your materials to live in about a week': 'From your materials to live in about a week',
          'Hand-coded and phone-first, with the copy tightened and the contact points placed where they get tapped.': 'Hand-coded and phone-first, with the copy tightened and the contact points placed where they get tapped.',
          'The page you want, the address, your content, how buyers reach you, your business details and when you want to start.': 'The page you want, the address, your content, how buyers reach you, your business details and when you want to start.',
          'monthly fees': 'monthly fees',
          'one time': 'one time',
          'On your .vxms.dev address with HTTPS on, and nothing to renew afterwards.': 'On your .vxms.dev address with HTTPS on, and nothing to renew afterwards.',
          'One round of changes is included, and it stays open until the page goes live.': 'One round of changes is included, and it stays open until the page goes live.',
          'The page goes live': 'The page goes live',
          'to go live': 'to go live',
          'We build the page': 'We build the page',
          'You read it through': 'You read it through',
          'You send the six items': 'You send the six items',
          'One page that does exactly one job and nothing else: turning the visitors who land on it into genuine, qualified enquiries for your business.': 'One page that does exactly one job and nothing else: turning the visitors who land on it into genuine, qualified enquiries for your business.',
          'RM 299 landing page offer': 'RM 299 landing page offer',
          'One page should not lock you into a year': '**One page** should not lock you into a year',
          'Most web design quotes cover a multi-page website billed across the year. Need only one page? RM 299 covers it once, hosting and SSL included.': 'Most web design quotes cover a multi-page website billed across the year. Need only one page? RM 299 covers it once, hosting and SSL included.',
          'On a web design plan': 'On a web design plan',
          'a year, on annual billing': 'a year, on annual billing',
          'The Startup plan is RM 58 a month on annual billing, with a 12-month minimum. That buys a multi-page site with business email, CDN, backups and support.': 'The Startup plan is RM 58 a month on annual billing, with a 12-month minimum. That buys a multi-page site with business email, CDN, backups and support.',
          'This promo': 'This promo',
          'once, nothing monthly': 'once, nothing monthly',
          'Both columns cover your first year. One is a yearly contract for a multi-page website; the other is a single one-off payment that never renews.': 'Both columns cover your first year. One is a yearly contract for a multi-page website; the other is a single one-off payment that never renews.',
          'RM 696 a year, or RM 299 once': 'RM 696 a year, or RM 299 once',
          'One hand-coded landing page. Your address, hosting and SSL stay in the package, and nothing renews.': 'One hand-coded landing page. Your address, hosting and SSL stay in the package, and nothing renews.',
          'One page, one payment': 'One page, one payment',
          'Promotion expired': 'Promotion expired',
          'Everything in the RM 299 package': '**Everything** in the RM 299 package',
          'No small print, no asterisk, no renewal invoice afterwards. Here is precisely what you get once the page goes live and the work is finished.': 'No small print, no asterisk, no renewal invoice afterwards. Here is precisely what you get once the page goes live and the work is finished.',
          'Your own .vxms.dev address': 'Your own .vxms.dev address',
          'A secondary domain that keeps running for the life of the service. No renewal and no domain bill.': 'A secondary domain that keeps running for the life of the service. No renewal and no domain bill.',
          'Hosting included, for good': 'Hosting included, for good',
          'Hosting for the single page. There is no renewal fee and no yearly invoice.': 'Hosting for the single page. There is no renewal fee and no yearly invoice.',
          'SSL from day one': 'SSL from day one',
          'HTTPS on the page the moment it goes live, so browsers stop warning your visitors.': 'HTTPS on the page the moment it goes live, so browsers stop warning your visitors.',
          'Contact form and WhatsApp': 'Contact form and WhatsApp',
          'Two ways for a visitor to reach you, both straight from the page.': 'Two ways for a visitor to reach you, both straight from the page.',
          'Built for the phone': 'Built for the phone',
          'Designed phone-first, because that is where most of your visitors will arrive.': 'Designed phone-first, because that is where most of your visitors will arrive.',
          'Hand-coded, not a template': 'Hand-coded, not a template',
          'No WordPress, no Wix, no drag and drop builder. Just the page, written for your business.': 'No WordPress, no Wix, no drag and drop builder. Just the page, written for your business.',
          'Send these straight over and we start immediately. The sooner they reach us, the sooner the page goes live and starts working properly for you.': 'Send these straight over and we start immediately. The sooner they reach us, the sooner the page goes live and starts working properly for you.',
          'The page you want': 'The page you want',
          'One landing page, built around a single job. More than one page is a web design plan.': 'One landing page, built around a single job. More than one page is a web design plan.',
          'The name in front of .vxms.dev, live when we publish.': 'The name in front of .vxms.dev, live when we publish.',
          'What the RM 299 package actually includes, what remains with you for life, and exactly what happens on the day the page eventually goes live.': 'What the RM 299 package actually includes, what remains with you for life, and exactly what happens on the day the page eventually goes live.',
          'What do I actually get for RM 299?': 'What do I actually get for RM 299?',
          'Why is this cheaper than a web design plan?': 'Why is this cheaper than a web design plan?',
          'Is there any recurring fee or renewal?': 'Is there any recurring fee or renewal?',
          'What does lifetime service mean?': 'What does lifetime service mean?',
          'Can I use my own custom domain later?': 'Can I use my own custom domain later?',
          'Can I make changes after the page goes live?': 'Can I make changes after the page goes live?',
          'Is this WordPress or Wix?': 'Is this WordPress or Wix?',
          'How long does delivery take?': 'How long does delivery take?',
          'Can you design more than one page or a full website?': 'Can you design more than one page or a full website?',
          'faq-what': 'One hand-coded landing page on your own .vxms.dev address, with hosting and SSL included for the life of the service. A contact form and a WhatsApp link are built in, and the page is written to bring in enquiries, not just to look good.',
          'faq-cheaper': 'The Startup plan is RM 58 a month on annual billing with a 12-month minimum, which is RM 696 in the first year. That plan covers a multi-page website with business email, CDN, backups and support. If you only need one page, you do not need any of it.',
          'faq-lifetime': 'As long as Veximus is in business, we provide your secondary domain, hosting, and SSL certificate at no extra charge, for the life of the service.',
          'faq-domain': 'This promo does not support using your own custom domain. If you need a top-level domain such as .com or .my, or want to bring your own domain, that is a Startup or Growth web design plan.',
          'faq-recurring': 'No. RM 299 is a one-time payment. The included secondary domain, hosting, and SSL stay in the package for life, with no hidden fees and no renewal cost for what is included.',
          'faq-changes': 'This is a special promo, so updates are only available until the site goes live. After that, no further changes to the content can be made.',
          'faq-platform': 'No. All landing pages are hand-coded, not on WordPress, Wix, or any template system.',
          'faq-delivery': 'Typical delivery is within 1 week after we receive all required materials from you.',
          'faq-pages': 'This promo is for a single landing page. If you need a multi-page website, that is a Startup or Growth web design plan.'
        },
        'zh-MY': {
          'One hand-coded landing page for RM 299. Hosting, your own .vxms.dev address and SSL included, with nothing monthly. Live in about a week.': 'RM 299 一套手写代码落地页。托管、您专属的 .vxms.dev 地址与 SSL 都包含在内，没有月费。约一周上线。',
          '1 week': '约 1 周',
          'Day 0': '第 0 天',
          'Day 1': '第 1 天',
          'Day 4': '第 4 天',
          'Day 5': '第 5 天',
          'Four straightforward steps, from the day you send your materials across to the day the page is finally live, and we keep you posted throughout.': '从您把资料交给我们的那天算起，到页面上线的那天为止，一共四个步骤，每一个阶段我们都会主动向您更新进度，让您随时掌握，绝不让您追着问。',
          'From your materials to live in about a week': '从资料到上线，约**一周**',
          'Hand-coded and phone-first, with the copy tightened and the contact points placed where they get tapped.': '手写代码、以手机为先，文案经我们收紧，联系方式放在访客会点的地方。',
          'The page you want, the address, your content, how buyers reach you, your business details and when you want to start.': '您要的页面、您想要的网址、您的内容、客户如何联系您、您的营业资料，以及您想开始的时间。',
          'monthly fees': '月费',
          'one time': '一次付清',
          'On your .vxms.dev address with HTTPS on, and nothing to renew afterwards.': '放在您的 .vxms.dev 地址上，HTTPS 即刻启用，之后没有任何续费。',
          'One round of changes is included, and it stays open until the page goes live.': '包含一轮修改，在上线之前都可以提出。',
          'The page goes live': '页面上线',
          'to go live': '即可上线',
          'We build the page': '我们开始制作',
          'You read it through': '您过一遍',
          'You send the six items': '您交来六项资料',
          'One page that does exactly one job and nothing else: turning the visitors who land on it into genuine, qualified enquiries for your business.': '一个页面只专心做一件事：把来到这个页面的访客，变成真正有意向的询盘。它不承担别的任务，只负责让有兴趣的人主动联系您，而且越快越好。',
          'RM 299 landing page offer': 'RM 299 落地页优惠',
          'One page should not lock you into a year': '**一个页面**不该把您绑住一整年',
          'Most web design quotes cover a multi-page website billed across the year. Need only one page? RM 299 covers it once, hosting and SSL included.': '市面上的网页设计报价，多半是为多页网站而设，而且按年收费。您只需要一个页面吗？RM 299 一次付清，托管与 SSL 都包含在内。',
          'On a web design plan': '网页设计配套',
          'a year, on annual billing': '一年，按年缴付',
          'The Startup plan is RM 58 a month on annual billing, with a 12-month minimum. That buys a multi-page site with business email, CDN, backups and support.': '启动配套按年缴付为每月 RM 58，最少 12 个月。这笔费用换来的是多页网站，外加企业电邮、CDN、备份与支援。',
          'This promo': '本优惠',
          'once, nothing monthly': '一次付清，无月费',
          'Both columns cover your first year. One is a yearly contract for a multi-page website; the other is a single one-off payment that never renews.': '两边算的都是您的第一年。一边是多页网站的一年合约，另一边是永不续费的一次付清，两条长条按比例画，差距一眼就看得出来，不必细看数字。',
          'RM 696 a year, or RM 299 once': '一年 RM 696，或一次付清 RM 299',
          'One hand-coded landing page. Your address, hosting and SSL stay in the package, and nothing renews.': '一个手写代码落地页。地址、托管与 SSL 都留在配套内，没有任何项目需要续费。',
          'One page, one payment': '一个页面，一次付费',
          'Promotion expired': '优惠已结束',
          'Everything in the RM 299 package': 'RM 299 配套的**全部内容**',
          'No small print, no asterisk, no renewal invoice afterwards. Here is precisely what you get once the page goes live and the work is finished.': '没有小字备注，也没有等着您的续费账单。页面上线、工作完成之后，您会拿到的东西，都清清楚楚列在这里，一项不少，也没有事后才追加的费用。',
          'Your own .vxms.dev address': '属于您的 .vxms.dev 地址',
          'A secondary domain that keeps running for the life of the service. No renewal and no domain bill.': '二级域名，在服务期间持续有效。无需续费，也没有域名账单。',
          'Hosting included, for good': '托管已包含，长期有效',
          'Hosting for the single page. There is no renewal fee and no yearly invoice.': '单一页面的托管。没有续费，也没有年度账单。',
          'SSL from day one': '第一天就有 SSL',
          'HTTPS on the page the moment it goes live, so browsers stop warning your visitors.': '页面上线即启用 HTTPS，浏览器不会再向访客发出警告。',
          'Contact form and WhatsApp': '联系表单与 WhatsApp',
          'Two ways for a visitor to reach you, both straight from the page.': '访客可直接从页面用两种方式联系您。',
          'Built for the phone': '为手机而建',
          'Designed phone-first, because that is where most of your visitors will arrive.': '以手机为先设计，因为大多数访客都是从手机进来的。',
          'Hand-coded, not a template': '手写代码，不是模板',
          'No WordPress, no Wix, no drag and drop builder. Just the page, written for your business.': '不用 WordPress、不用 Wix、不用拖放建站工具。只有为您业务而写的这个页面。',
          'Send these straight over and we start immediately. The sooner they reach us, the sooner the page goes live and starts working properly for you.': '把以下资料发给我们，我们收到就立刻开工，绝不拖延。您越早交齐这些资料，页面上线就越快，也越早开始替您带来询盘，让生意早一点找上门。',
          'The page you want': '您要的页面',
          'One landing page, built around a single job. More than one page is a web design plan.': '一个落地页，围绕单一目标来建。若需要多于一个页面，那就属于网页设计配套。',
          'The name in front of .vxms.dev, live when we publish.': '放在 .vxms.dev 前面的名称，发布时即刻生效。',
          'What the RM 299 package actually includes, what remains with you for life, and exactly what happens on the day the page eventually goes live.': 'RM 299 配套究竟包含什么、哪些东西终身保留，以及页面上线当天到底会发生什么、后续还有没有费用，都清楚列在下面，让您心里有个数。',
          'What do I actually get for RM 299?': 'RM 299 实际上得到什么？',
          'Why is this cheaper than a web design plan?': '为什么这比网页设计配套便宜？',
          'Is there any recurring fee or renewal?': '之后还要续费吗？',
          'What does lifetime service mean?': '终身服务是什么意思？',
          'Can I use my own custom domain later?': '之后可以用自己的域名吗？',
          'Can I make changes after the page goes live?': '上线后还可以改页面吗？',
          'Is this WordPress or Wix?': '这是 WordPress 还是 Wix？',
          'How long does delivery take?': '多久可以交付？',
          'Can you design more than one page or a full website?': '可以做多页或完整网站吗？',
          'faq-what': '一个手写代码落地页，放在属于您的 .vxms.dev 地址上，托管与 SSL 在服务期间都包含在内。内建联系表单与 WhatsApp 链接，文案以带来询盘为目标，而不只是好看。',
          'faq-cheaper': '启动配套按年缴付为每月 RM 58，最少 12 个月，也就是第一年 RM 696。该配套涵盖多页网站，外加企业电邮、CDN、备份与支援。若您只需要一个页面，这些您都用不上。',
          'faq-lifetime': '只要 Veximus 持续营运，我们就会为您提供二级域名、托管与 SSL 证书，不另收费，直至服务终止。',
          'faq-domain': '本优惠不支持使用您自有的域名。若需要 .com 或 .my 等顶级域名，或要使用自有域名，请选择启动或成长网页设计配套。',
          'faq-recurring': '没有。RM 299 为一次付清。配套内的二级域名、托管与 SSL 终身包含，所含项目无隐藏费用、无续费。',
          'faq-changes': '这是特别优惠，仅在网站上线前可更新。上线后不能再改内容。',
          'faq-platform': '不是。所有落地页均为手写代码，不是 WordPress、Wix 或任何模板系统。',
          'faq-delivery': '我们收到您提供的全部所需资料后，通常约一周交付。',
          'faq-pages': '本优惠只含一个落地页。若需要多页网站，请选择启动或成长网页设计配套。'
        },
        'ms-MY': {
          'One hand-coded landing page for RM 299. Hosting, your own .vxms.dev address and SSL included, with nothing monthly. Live in about a week.': 'Satu halaman pendaratan dikod tangan untuk RM 299. Hosting, alamat .vxms.dev anda sendiri dan SSL disertakan, tanpa bayaran bulanan. Dalam talian sekitar seminggu.',
          '1 week': '1 minggu',
          'Day 0': 'Hari 0',
          'Day 1': 'Hari 1',
          'Day 4': 'Hari 4',
          'Day 5': 'Hari 5',
          'Four straightforward steps, from the day you send your materials across to the day the page is finally live, and we keep you posted throughout.': 'Empat langkah dari hari anda menghantar bahan sehingga hari halaman itu aktif, dan kami akan memaklumkan perkembangan pada setiap peringkat.',
          'From your materials to live in about a week': 'Dari bahan anda kepada halaman aktif dalam sekitar seminggu',
          'Hand-coded and phone-first, with the copy tightened and the contact points placed where they get tapped.': 'Dikod tangan dan diutamakan telefon, dengan salinan dikemaskan dan titik hubungan diletak di tempat orang menekannya.',
          'The page you want, the address, your content, how buyers reach you, your business details and when you want to start.': 'Halaman yang anda mahu, alamat yang anda mahu, kandungan anda, cara pembeli menghubungi anda, butiran perniagaan anda dan bila anda mahu mula.',
          'monthly fees': 'yuran bulanan',
          'one time': 'sekali bayar',
          'On your .vxms.dev address with HTTPS on, and nothing to renew afterwards.': 'Pada alamat .vxms.dev anda dengan HTTPS aktif, dan tiada apa perlu diperbaharui selepas itu.',
          'One round of changes is included, and it stays open until the page goes live.': 'Satu pusingan perubahan disertakan, dan ia terbuka sehingga halaman itu aktif.',
          'The page goes live': 'Halaman mula aktif',
          'to go live': 'untuk mula aktif',
          'We build the page': 'Kami bina halaman itu',
          'You read it through': 'Anda baca keseluruhannya',
          'You send the six items': 'Anda hantar enam perkara itu',
          'One page that does exactly one job and nothing else: turning the visitors who land on it into genuine, qualified enquiries for your business.': 'Satu halaman yang melakukan satu tugas sahaja: menukar pelawat yang sampai ke situ menjadi pertanyaan sebenar untuk perniagaan anda hari ini.',
          'RM 299 landing page offer': 'Tawaran halaman pendaratan RM 299',
          'One page should not lock you into a year': '**Satu halaman** tidak sepatutnya mengikat anda untuk setahun',
          'Most web design quotes cover a multi-page website billed across the year. Need only one page? RM 299 covers it once, hosting and SSL included.': 'Harga reka bentuk web biasanya untuk laman banyak halaman, dibilkan setahun. Perlukan satu halaman? RM 299 meliputi sekali, hosting dan SSL termasuk.',
          'On a web design plan': 'Pada pelan reka bentuk web',
          'a year, on annual billing': 'setahun, pada bil tahunan',
          'The Startup plan is RM 58 a month on annual billing, with a 12-month minimum. That buys a multi-page site with business email, CDN, backups and support.': 'Pelan Startup ialah RM 58 sebulan pada bil tahunan, dengan minimum 12 bulan. Itu membeli laman berbilang halaman dengan e-mel perniagaan, CDN, sandaran dan sokongan.',
          'This promo': 'Promosi ini',
          'once, nothing monthly': 'sekali bayar, tiada bulanan',
          'Both columns cover your first year. One is a yearly contract for a multi-page website; the other is a single one-off payment that never renews.': 'Kedua-dua lajur meliputi tahun pertama. Satu kontrak tahunan untuk laman banyak halaman; satu lagi bayaran sekali yang tidak pernah diperbaharui.',
          'RM 696 a year, or RM 299 once': 'RM 696 setahun, atau RM 299 sekali',
          'One hand-coded landing page. Your address, hosting and SSL stay in the package, and nothing renews.': 'Satu halaman pendaratan dikod tangan. Alamat, hosting dan SSL anda kekal dalam pakej, dan tiada apa yang diperbaharui.',
          'One page, one payment': 'Satu halaman, satu bayaran',
          'Promotion expired': 'Promosi telah tamat',
          'Everything in the RM 299 package': '**Semua** dalam pakej RM 299',
          'No small print, no asterisk, no renewal invoice afterwards. Here is precisely what you get once the page goes live and the work is finished.': 'Tiada nota kecil dan tiada invois pembaharuan menanti anda. Inilah yang anda terima apabila halaman itu aktif dan semua kerja selesai sepenuhnya.',
          'Your own .vxms.dev address': 'Alamat .vxms.dev milik anda',
          'A secondary domain that keeps running for the life of the service. No renewal and no domain bill.': 'Domain sekunder yang terus berjalan sepanjang hayat perkhidmatan. Tiada pembaharuan dan tiada bil domain.',
          'Hosting included, for good': 'Hosting disertakan, untuk selamanya',
          'Hosting for the single page. There is no renewal fee and no yearly invoice.': 'Hosting untuk halaman tunggal. Tiada yuran pembaharuan dan tiada invois tahunan.',
          'SSL from day one': 'SSL dari hari pertama',
          'HTTPS on the page the moment it goes live, so browsers stop warning your visitors.': 'HTTPS pada halaman sebaik ia dalam talian, supaya pelayar berhenti memberi amaran kepada pelawat anda.',
          'Contact form and WhatsApp': 'Borang hubungan dan WhatsApp',
          'Two ways for a visitor to reach you, both straight from the page.': 'Dua cara untuk pelawat menghubungi anda, kedua-duanya terus dari halaman.',
          'Built for the phone': 'Dibina untuk telefon',
          'Designed phone-first, because that is where most of your visitors will arrive.': 'Direka untuk telefon dahulu, kerana di situlah kebanyakan pelawat anda akan tiba.',
          'Hand-coded, not a template': 'Dikod tangan, bukan templat',
          'No WordPress, no Wix, no drag and drop builder. Just the page, written for your business.': 'Bukan WordPress, bukan Wix, tiada pembina seret dan lepas. Hanya halaman ini, ditulis untuk perniagaan anda.',
          'Send these straight over and we start immediately. The sooner they reach us, the sooner the page goes live and starts working properly for you.': 'Hantar semuanya dan kami mula serta-merta. Lebih cepat kami terima, lebih cepat halaman itu aktif dan terus mula bekerja sepenuhnya untuk anda.',
          'The page you want': 'Halaman yang anda mahu',
          'One landing page, built around a single job. More than one page is a web design plan.': 'Satu halaman pendaratan, dibina untuk satu tugas. Lebih daripada satu halaman ialah pelan reka bentuk web.',
          'The name in front of .vxms.dev, live when we publish.': 'Nama di hadapan .vxms.dev, aktif apabila kami terbitkan.',
          'What the RM 299 package actually includes, what remains with you for life, and exactly what happens on the day the page eventually goes live.': 'Apa yang pakej RM 299 sebenarnya sertakan, apa yang kekal bersama anda seumur hidup, dan apa yang benar-benar berlaku pada hari halaman itu aktif.',
          'What do I actually get for RM 299?': 'Apa sebenarnya yang saya dapat untuk RM 299?',
          'Why is this cheaper than a web design plan?': 'Mengapa ini lebih murah daripada pelan reka bentuk web?',
          'Is there any recurring fee or renewal?': 'Adakah yuran berulang atau pembaharuan?',
          'What does lifetime service mean?': 'Apa maksud perkhidmatan seumur hidup?',
          'Can I use my own custom domain later?': 'Boleh saya guna domain tersuai sendiri kemudian?',
          'Can I make changes after the page goes live?': 'Boleh saya buat perubahan selepas halaman dalam talian?',
          'Is this WordPress or Wix?': 'Adakah ini WordPress atau Wix?',
          'How long does delivery take?': 'Berapa lama penyerahan?',
          'Can you design more than one page or a full website?': 'Boleh anda reka lebih daripada satu halaman atau laman web penuh?',
          'faq-what': 'Satu halaman pendaratan dikod tangan pada alamat .vxms.dev milik anda, dengan hosting dan SSL disertakan sepanjang hayat perkhidmatan. Borang hubungan dan pautan WhatsApp dibina dalam, dan halamannya ditulis untuk menarik pertanyaan, bukan sekadar kelihatan cantik.',
          'faq-cheaper': 'Pelan Startup ialah RM 58 sebulan pada bil tahunan dengan minimum 12 bulan, iaitu RM 696 pada tahun pertama. Pelan itu meliputi laman berbilang halaman dengan e-mel perniagaan, CDN, sandaran dan sokongan. Jika anda hanya perlukan satu halaman, anda tidak perlukan semua itu.',
          'faq-lifetime': 'Selagi Veximus beroperasi, kami sediakan domain sekunder, hosting dan sijil SSL anda tanpa caj tambahan, sepanjang hayat perkhidmatan.',
          'faq-domain': 'Promosi ini tidak menyokong domain tersuai anda sendiri. Jika anda perlukan domain peringkat atasan seperti .com atau .my, atau mahu bawa domain sendiri, itu pelan reka bentuk web Startup atau Growth.',
          'faq-recurring': 'Tidak. RM 299 ialah bayaran sekali. Domain sekunder, hosting dan SSL yang disertakan kekal dalam pakej seumur hidup, tanpa yuran tersembunyi dan tanpa kos pembaharuan untuk yang disertakan.',
          'faq-changes': 'Ini promosi khas, jadi kemas kini hanya tersedia sehingga laman dalam talian. Selepas itu, tiada perubahan lanjut pada kandungan.',
          'faq-platform': 'Tidak. Semua halaman pendaratan dikod tangan, bukan pada WordPress, Wix, atau mana-mana sistem templat.',
          'faq-delivery': 'Penyerahan biasa dalam 1 minggu selepas kami terima semua bahan yang diperlukan daripada anda.',
          'faq-pages': 'Promosi ini untuk satu halaman pendaratan. Jika anda perlukan laman web berbilang halaman, itu pelan reka bentuk web Startup atau Growth.'
        }
      }
    });

    /* The three numbers that decide the offer, closing the comparison band. */
    const stats = computed(() => [
      { value: 'RM 299', label: t('one time') },
      { value: t('1 week'), label: t('to go live') },
      { value: '0', label: t('monthly fees') }
    ]);

    /* The comparison. A full year on a plan against one payment, each bar
       expressed as a share of the RM 696 the plan costs in the first year, so
       the bars stay to scale if a rate ever changes rather than being
       hardcoded widths. One bar per option on this page; the shape is the
       shared Compare band's, so the annual pack page can plot a pair. */
    const panels = computed(() => {
      const yearOnPlan = 696;

      return [
        { name: t('On a web design plan'), label: t('a year, on annual billing'), value: 'RM 696', amount: 696, flat: true, save: '', note: t('The Startup plan is RM 58 a month on annual billing, with a 12-month minimum. That buys a multi-page site with business email, CDN, backups and support.') },
        { name: t('This promo'), label: t('once, nothing monthly'), value: 'RM 299', amount: 299, flat: false, save: 'RM 397', note: t('One hand-coded landing page. Your address, hosting and SSL stay in the package, and nothing renews.') }
      ].map((option) => ({
        name: option.name,
        save: option.save,
        note: option.note,
        bars: [
          {
            label: option.label,
            value: option.value,
            share: Math.round((option.amount / yearOnPlan) * 100),
            flat: option.flat
          }
        ]
      }));
    });

    /* The delivery window. Four steps, so the page answers when the thing
       arrives and not only what it costs. */
    const week = computed(() => [
      { when: t('Day 0'), title: t('You send the six items'), body: t('The page you want, the address, your content, how buyers reach you, your business details and when you want to start.') },
      { when: t('Day 1'), title: t('We build the page'), body: t('Hand-coded and phone-first, with the copy tightened and the contact points placed where they get tapped.') },
      { when: t('Day 4'), title: t('You read it through'), body: t('One round of changes is included, and it stays open until the page goes live.') },
      { when: t('Day 5'), title: t('The page goes live'), body: t('On your .vxms.dev address with HTTPS on, and nothing to renew afterwards.') }
    ]);

    const included = computed(() => [
      { title: t('Your own .vxms.dev address'), body: t('A secondary domain that keeps running for the life of the service. No renewal and no domain bill.') },
      { title: t('Hosting included, for good'), body: t('Hosting for the single page. There is no renewal fee and no yearly invoice.') },
      { title: t('SSL from day one'), body: t('HTTPS on the page the moment it goes live, so browsers stop warning your visitors.') },
      { title: t('Contact form and WhatsApp'), body: t('Two ways for a visitor to reach you, both straight from the page.') },
      { title: t('Built for the phone'), body: t('Designed phone-first, because that is where most of your visitors will arrive.') },
      { title: t('Hand-coded, not a template'), body: t('No WordPress, no Wix, no drag and drop builder. Just the page, written for your business.') }
    ]);

    /* Slots three to six are lifted verbatim from the annual pack brief, body and
       all, so the two promo pages ask for the same things in the same words and
       the translated copy matches line for line. Only the first two slots are the
       promo's own: RM 299 has one package and one .vxms.dev address, so those are
       the two places the offer genuinely differs. */
    const checklist = computed(() => [
      { title: t('The page you want'), body: t('One landing page, built around a single job. More than one page is a web design plan.') },
      { title: t('Your website address'), body: t('The name in front of .vxms.dev, live when we publish.') },
      { title: t('Your content'), body: t('Words, images and a logo in any form. We lay them out and tighten the copy.') },
      { title: t('How buyers reach you'), body: t('Phone, WhatsApp, email and address, placed where visitors can tap them.') },
      { title: t('Your business details'), body: t('The exact name and spelling, so the site, the email and the invoice all match.') },
      { title: t('A start date'), body: t('Tell us when you want to begin and we set the build window from there.') }
    ]);

    const faqs = computed(() => [
      { q: t('What do I actually get for RM 299?'), a: t('faq-what') },
      { q: t('Why is this cheaper than a web design plan?'), a: t('faq-cheaper') },
      { q: t('Is there any recurring fee or renewal?'), a: t('faq-recurring') },
      { q: t('What does lifetime service mean?'), a: t('faq-lifetime') },
      { q: t('Can I use my own custom domain later?'), a: t('faq-domain') },
      { q: t('Can I make changes after the page goes live?'), a: t('faq-changes') },
      { q: t('Is this WordPress or Wix?'), a: t('faq-platform') },
      { q: t('How long does delivery take?'), a: t('faq-delivery') },
      { q: t('Can you design more than one page or a full website?'), a: t('faq-pages') }
    ]);

    return { t, checklist, faqs, included, panels, stats, week };
  }
};
</script>
